import * as React from 'react';

import {
  BrokeredOrderSortOption,
  enumBrokeredOrderSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  DataGrid,
  FloatingToolbar,
  ListScreenLayout,
  Pagination,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle, LucideTrash, LucideX } from 'lucide-react';

import * as state from '../../_state/brokeredOrdersList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { brokerageRoutePaths } from '../../routes';
import { BrokeredOrderRecord } from '../../types/brokeredOrder';

import { BrokeredOrderCreateDialog } from './BrokeredOrderCreateDialog';
import { BrokeredOrderFilters } from './BrokeredOrderFilters';
import { useBrokeredOrderListColumns } from './hooks/useBrokeredOrderListColumns';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: BrokeredOrderSortOption; desc: BrokeredOrderSortOption }
> = {
  orderNumber: {
    asc: enumBrokeredOrderSortOption.OrderNumberAsc,
    desc: enumBrokeredOrderSortOption.OrderNumberDesc,
  },
  status: {
    asc: enumBrokeredOrderSortOption.StatusAsc,
    desc: enumBrokeredOrderSortOption.StatusDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ BROKERED ORDER LIST VIEW ⚛️ ⚛️ ⚛️
//
export const BrokeredOrderListView = () => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();

  // Atoms
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteBrokeredOrders,
  );
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const setCurrentSorting = useSetAtom(state.$currentSorting);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCreateDialogOpen,
  );

  // Other data hooks
  const { columns, columnVisibilityState } = useBrokeredOrderListColumns({
    onRowClick: (order: BrokeredOrderRecord) => {
      setLocation(brokerageRoutePaths.orderDetail, {
        params: { id: order.id },
      });
    },
    onRefetch: () => refetch?.(),
  });

  const brokeredOrders = data?.brokeredOrders ?? [];
  const total = data?.totalCount ?? 0;

  // Memoized values
  const emptyPanelProps = React.useMemo(
    () => ({
      title: t.orders.list.empty.title,
      description: <span>{t.orders.list.empty.description}</span>,
      action: (
        <Button
          onPress={() => setIsCreateDialogOpen(true)}
          variant="solid"
          color="primary"
          icon={<LucidePlusCircle />}
        >
          {t.orders.create.submitButton}
        </Button>
      ),
      centered: true,
    }),
    [t, setIsCreateDialogOpen],
  );

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumBrokeredOrderSortOption.CreatedAtDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleClearSelection = React.useCallback(() => {
    setSelectedKeys(new Set());
  }, [setSelectedKeys]);

  const handleCreateOrder = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);

  return (
    <>
      <ListScreenLayout
        filters={<BrokeredOrderFilters />}
        actions={
          <Button
            onPress={handleCreateOrder}
            variant="solid"
            color="primary"
            icon={<LucidePlusCircle />}
          >
            {t.orders.list.createButton}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
        className="@BrokeredOrderListView"
      >
        <>
          <DataGrid<BrokeredOrderRecord>
            isLoading={loadingState.isInitialLoading || isFetching}
            columns={columns}
            dataSource={brokeredOrders}
            rowKey="id"
            aria-label={t.orders.list.ariaLabel}
            selectionMode="multiple"
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            onColumnSort={handleColumnSort}
            columnVisibilityState={columnVisibilityState}
            className="w-full cursor-pointer text-sm"
            fullWidth
            persistColumnOrderKey="brokerage:orders-list"
            hasError={!!error}
            errorPanelProps={
              error
                ? {
                    description: t.common.errors.loading(
                      'brokered orders',
                      error.message,
                    ),
                    centered: true,
                  }
                : undefined
            }
            emptyPanelProps={emptyPanelProps}
          />
          <FloatingToolbar visible={Array.from(selectedKeys).length > 0}>
            <div className="flex flex-row items-center justify-between gap-3">
              <div className="flex flex-row items-center gap-2 text-sm">
                <Badge color="primary" size="small">
                  {Array.from(selectedKeys).length}
                </Badge>{' '}
                {t.common.selected}
              </div>
              <div className="flex flex-row items-center gap-2">
                <Button variant="outline" icon={<LucideTrash />} isDisabled>
                  {t.common.actions.void}
                </Button>
                <LucideX
                  className="size-4 cursor-pointer text-neutral/80"
                  onClick={handleClearSelection}
                />
              </div>
            </div>
          </FloatingToolbar>
        </>
      </ListScreenLayout>
      <BrokeredOrderCreateDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={() => refetch?.()}
      />
    </>
  );
};
